/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { SKELETON_BLOCK, skeletonSlots } from './skeleton-primitives';

export interface KanbanSkeletonProps {
  /** Number of board columns to render. */
  columns?: number;
  /** Number of card placeholders per column. */
  cardsPerColumn?: number;
}

/**
 * Placeholder mimicking a kanban board of columns with stacked cards.
 *
 * @param props Column count and cards per column.
 * @returns A shimmering kanban placeholder.
 */
export const KanbanSkeleton: React.FC<KanbanSkeletonProps> = ({
  columns = 3,
  cardsPerColumn = 3,
}) => (
  <div data-testid="kanban-skeleton" className="flex gap-4 overflow-hidden">
    {skeletonSlots(columns).map((column) => (
      <div
        key={column}
        data-testid="kanban-skeleton-column"
        className="flex w-72 flex-col gap-3 rounded-(--radius-lg) bg-[var(--surface-secondary)] p-3"
      >
        <div className={`${SKELETON_BLOCK} h-4 w-1/2`} />
        {skeletonSlots(cardsPerColumn).map((card) => (
          <div key={card} className={`${SKELETON_BLOCK} h-16 w-full`} />
        ))}
      </div>
    ))}
  </div>
);
